<template>
  <div class="formulario-modelo">
    <!-- Overlay de carga -->
    <LoadingOverlay :visible="loadingInicial" message="Cargando componentes..." />
    <LoadingOverlay :visible="loadingAccion" message="Procesando acción..." />

    <!-- Contenido normal -->
    <div v-if="!loadingInicial">
      <div class="header-flex">
        <div>
          <h1>Agregar Elementos</h1>
          <p v-if="nombreModelo" class="ctx-modelo">{{ nombreModelo }}</p>
        </div>
        <button
          class="btn guardar"
          @click.prevent="mostrarConfirmar = true"
        >
          Finalizar
        </button>
      </div>

      <!-- Filtro de componentes -->
      <div class="filtro-botones">
        <button
          :class="['btn-filtro', tipoFiltro === 'todos' ? 'activo' : '']"
          @click="tipoFiltro = 'todos'; componenteNivel1Filtro = null"
        >
          Mostrar todo el modelo
        </button>
        <button
          :class="['btn-filtro', tipoFiltro === 'por-componente' ? 'activo' : '']"
          @click="tipoFiltro = 'por-componente'"
        >
          Por {{ nivelesArray[0] ?? 'Componente' }}
        </button>
      </div>
      <div v-if="tipoFiltro === 'por-componente'" class="filtro-select">
        <label for="componente-filtro">Selecciona un {{ nivelesArray[0]?.toLowerCase() ?? 'componente' }}:</label>
        <select v-model.number="componenteNivel1Filtro" id="componente-filtro" class="form-control">
          <option :value="null">-- Selecciona --</option>
          <option v-for="comp in componentesNivel1" :key="comp.id_componente" :value="comp.id_componente">
            {{ comp.ordinal }} - {{ comp.nombre }}
          </option>
        </select>
      </div>

      <div class="marco">
        <div v-if="(store.componentes[0]?.children ?? []).length === 0" class="mensaje-vacio">
          <p>El Modelo no tiene elementos aún agregados</p>
        </div>
        <ModificarComponentes
          v-else
          :componentes="componentesFiltrados"
          :idComponenteRaiz="idComponenteRaiz"
          :componenteEditandoId="componenteEditando?.id_componente ?? null"
          :niveles="nivelesArray"
          :obtenerNumeracion="obtenerNumeracionJerarquica"
          @modificar="onModificarComponente"
          @eliminar="onEliminarComponente"
          @agregarHijo="onAgregarHijo"
          @reordenar="onReordenarComponente"
        />

        <!-- Modal para agregar componente nivel 1 -->
        <FormularioComponente
          :visible="mostrarFormNivel1"
          modo="agregarNivel1"
          :loading="loading"
          :nombreNivelSiguiente="nivelesArray[0] ?? 'Componente'"
          @aceptar="onAceptarNivel1"
          @cancelar="cancelarNivel1"
        />

        <!-- Modal para agregar hijo -->
        <FormularioComponente
          :visible="mostrarFormHijo"
          modo="agregarHijo"
          :loading="loading"
          :nombrePadre="padreSeleccionado?.nombre ?? ''"
          :nombreNivelSiguiente="nombreNivelSiguiente"
          @aceptar="onAceptarHijo"
          @cancelar="cancelarHijo"
        />

        <!-- Modal para modificar -->
        <FormularioComponente
          :visible="mostrarFormModificar"
          modo="modificar"
          :loading="loading"
          :nombreComponente="componenteEditando?.nombre ?? ''"
          :nombreInicial="nombreModificar"
          :descripcionInicial="descripcionModificar"
          :enfoqueInicial="enfoqueModificar"
          :grupoInicial="grupoModificar"
          @aceptar="onAceptarModificar"
          @cancelar="cancelarModificar"
        />
      </div>

      <!-- Botón para mostrar formulario -->
      <div class="botones">
        <button class="btn guardar" @click="mostrarFormNivel1 = true" :disabled="loading || !idComponenteRaiz"> Agregar {{ nivelesArray[0] ?? 'Componente' }}</button>
        <button class="btn guardar" @click="irAGestionPesos"> Ajustar pesos </button>
      </div>
      <div class="botones">
        <button class="btn salir" @click="salir">Salir</button>
        <MensajeConfirmacion
          :visible="mostrarConfirmar"
          title="Confirmar acción"
          message="¿Desea finalizar los ajustes del modelo?"
          confirmText="Aceptar"
          cancelText="Cancelar"
          :confirmLoading="loadingAccion"
          @confirm="CompletarModelo"
          @cancel="mostrarConfirmar = false"
        />
      </div>
    </div>
    <!-- Mensaje flotante -->
    <MensajeFlotante
      :visible="!!feedback"
      :mensaje="feedback"
      :tipo="feedbackEsPositivo ? 'positivo' : 'error'"
      @cerrar="feedback = ''"
    />
    <MensajeConfirmacion
      :visible="mostrarConfirmarEliminar"
      title="Confirmar eliminación"
      :message="`¿Eliminar ${compSeleccionado?.nombre}?`"
      confirmText="Aceptar"
      cancelText="Cancelar"
      :confirmLoading="loadingAccion"
      @confirm="ejecutarEliminar"
      @cancel="mostrarConfirmarEliminar = false"
    />

  </div>
</template>

<script lang="ts" setup>
/**
 * Construcción del árbol de estructura del modelo.
 *
 * Solo operativa con el MODELO en estado `edicion`. Los cambios afectan a TODAS
 * las versiones, porque la estructura es del modelo y se comparte.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `/version/:id_version/componentes/agregar` (AgregarComponente) — permiso `componentes.crear`
 *   `/modelo/:id_modelo/componentes` (ComponentesModelo) — permiso `componentes.ver`
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useComponentes } from '@/store/GestionComponentes'
import { useGestionVersiones } from '@/store/GestionVersiones'
import useModelo from '@/store/GestionModelo'
import type Componente from '@/interfaces/Componente'
import MensajeConfirmacion from '@/components/MensajeConfirmacion.vue'
import ModificarComponentes from '@/components/ModificarComponentes.vue'
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import MensajeFlotante from '@/components/MensajeFlotante.vue'
import FormularioComponente from '@/components/FormularioComponente.vue'
import { limpiarNiveles } from '@/utils/nivelesHelper'

interface ComponenteAplanado {
  ref: Componente
  nivel: number
}

const route = useRoute()
const router = useRouter()
const store = useComponentes()
const versionStore = useGestionVersiones()
const modeloStore = useModelo()
const idVersion = Number(route.params.id_version ?? NaN)
const idModeloDirecto = Number(route.params.id_modelo ?? NaN)
const sinVersion = Number.isFinite(idModeloDirecto) && idModeloDirecto > 0
const loading = ref(false)
const feedback = ref('')
const feedbackEsPositivo = ref(false)
const mostrarConfirmar = ref(false)

// Filtro de componentes
const tipoFiltro = ref<'todos' | 'por-componente'>('todos')
const componenteNivel1Filtro = ref<number | null>(null)

const componentesNivel1 = computed(() => {
  const hijos = store.componentes[0]?.children ?? []
  return [...hijos].sort((a, b) =>
    (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
  )
})

const componentesFiltrados = computed(() => {
  const hijos = store.componentes[0]?.children ?? []
  if (tipoFiltro.value === 'por-componente' && componenteNivel1Filtro.value != null) {
    const comp = hijos.find(c => c.id_componente === componenteNivel1Filtro.value)
    return comp ? [comp] : []
  }
  return hijos
})

const nivelesArray = ref<string[]>([])
const idComponenteRaiz = ref<number | null>(null)
const idModelo = ref<number | null>(null)
const nombreModelo = ref('')
const loadingInicial = ref(true)
const loadingAccion = ref(false)  
const nombreNivelSiguiente = ref('')  
const componentesAplanados = ref<ComponenteAplanado[]>([])
const mostrarConfirmarEliminar = ref(false)
const compSeleccionado = ref<Componente | null>(null)

function nombreNivelDeComponente(comp: Componente): string {
  const aplanado = componentesAplanados.value.find(c => c.ref.id_componente === comp.id_componente)
  if (!aplanado) return 'Elemento'
  return nivelesArray.value[aplanado.nivel - 1] ?? 'Elemento'
}

function aplanarComponentes(arbol: Componente[], nivel = 0): ComponenteAplanado[] {
  const resultado: ComponenteAplanado[] = []
  for (const comp of arbol) {
    if (nivel > 0) {
      resultado.push({ ref: comp, nivel })
    }
    if ((comp.children?.length ?? 0) > 0) {
      resultado.push(...aplanarComponentes(comp.children!, nivel + 1))
    }
  }
  return resultado
}

function obtenerPadreRef(padreId: number | null | undefined): Componente | null {
  if (padreId == null) return null
  const padreAplanado = componentesAplanados.value.find(c => c.ref.id_componente === padreId)
  return padreAplanado?.ref ?? null
}

function obtenerNumeracionJerarquica(componente: Componente, nivel: number): string {
  const aplanado = componentesAplanados.value.find(c => c.ref.id_componente === componente.id_componente)
  if (!aplanado) return ''
  
  const numeros: number[] = []
  let actual: ComponenteAplanado | null = aplanado
  
  while (actual && actual.nivel > 0) {
    const padre = obtenerPadreRef(actual.ref.padre_id)
    if (!padre) {
      const hermanos = componentesAplanados.value.filter(c => c.nivel === 1)
      const hermanosSorted = hermanos.sort((a, b) => 
        (a.ref.ordinal ?? '').localeCompare(b.ref.ordinal ?? '', undefined, { numeric: true })
      )
      const posicion = hermanosSorted.findIndex(h => h.ref.id_componente === actual!.ref.id_componente)
      numeros.unshift(posicion + 1)
      break
    } else {
      const hermanos = padre.children ?? []
      const hermanosSorted = [...hermanos].sort((a, b) => 
        (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
      )
      const posicion = hermanosSorted.findIndex(h => h.id_componente === actual!.ref.id_componente)
      numeros.unshift(posicion + 1)
      
      const actualAplanado = componentesAplanados.value.find(c => c.ref.id_componente === padre.id_componente)
      actual = actualAplanado ?? null
    }
  }
  const numero = numeros.join('.')
  if (!numero) return ''
  // Usar el nivel real del árbol (aplanado.nivel es 1-based) para obtener el nombre correcto
  const nombreNivel = nivelesArray.value[aplanado.nivel - 1] ?? `Nivel ${aplanado.nivel}`
  return `${nombreNivel} ${numero}`
}


onMounted(async () => {
  if (!sinVersion && (!Number.isFinite(idVersion) || idVersion <= 0)) {
    feedback.value = 'ID de versión inválido.'
    feedbackEsPositivo.value = false
    return
  }

  loadingInicial.value = true
  try {
    if (sinVersion) {
      // Acceso directo por modelo (sin versión)
      const modelo = await modeloStore.getModeloById(idModeloDirecto)
      if (modelo?.niveles) {
        nivelesArray.value = limpiarNiveles(modelo.niveles)
      }
      nombreModelo.value = modelo?.nombre ?? ''
      idModelo.value = idModeloDirecto
    } else {
      const version = await versionStore.getVersionById(idVersion)
      if (version?.id_modelo) {
        const modelo = await modeloStore.getModeloById(version.id_modelo)
        if (modelo?.niveles) {
          nivelesArray.value = limpiarNiveles(modelo.niveles)
        }
        nombreModelo.value = modelo?.nombre ?? ''
      }
      // C-01: componentes pertenecen al modelo; cargamos por id_modelo
      idModelo.value = version?.id_modelo ?? null
    }
    if (!idModelo.value) {
      feedback.value = 'No se pudo obtener el modelo.'
      feedbackEsPositivo.value = false
      return
    }
    const raiz = await store.cargarRaizPorModelo(idModelo.value)
    if (!raiz) {
      feedback.value = 'No se encontró el componente raíz.'
      feedbackEsPositivo.value = false
      return
    }

    idComponenteRaiz.value = raiz.id_componente ?? null

    if (idComponenteRaiz.value != null) {
      const ok = await store.cargarArbolDesdeRaiz(idComponenteRaiz.value)
      if (!ok) {
        feedback.value = 'No fue posible cargar la versión y sus componentes.'
        feedbackEsPositivo.value = false
      } else {
        componentesAplanados.value = aplanarComponentes(store.componentes)
      }
    } else {
      feedback.value = 'No se encontró el ID de la raíz para cargar el árbol.'
      feedbackEsPositivo.value = false
    }
  } catch (e: any) {
    feedback.value = e?.message || 'Error al cargar la versión.'
    feedbackEsPositivo.value = false
  } finally {
    loadingInicial.value = false
  }
})

// Actualizar array aplanado cuando cambian los componentes
watch(
  () => store.componentes,
  () => {
    componentesAplanados.value = aplanarComponentes(store.componentes)
  },
  { deep: true }
)

// Formulario nivel 1
const mostrarFormNivel1 = ref(false)

function cancelarNivel1() {
  mostrarFormNivel1.value = false
}

async function onAceptarNivel1(payload: { nombre: string; descripcion: string; enfoque?: string; grupo_variante?: string | null }) {
  if (!idComponenteRaiz.value) return
  try {
    loading.value = true

    const datos = {
      nombre: payload.nombre,
      peso: 0,
      descripcion: payload.descripcion || null,
      enfoque: payload.enfoque ?? 'general',
      grupo_variante: payload.grupo_variante ?? null
    }

    const result = await store.agregarHijo(idComponenteRaiz.value, datos as any)
    if (!result.data) {
      feedback.value = result.error ?? `No fue posible crear el ${(nivelesArray.value[0] ?? 'elemento').toLowerCase()}.`
      feedbackEsPositivo.value = false
      cancelarNivel1()
      return
    }

    cancelarNivel1()
    feedback.value = `${nivelesArray.value[0] ?? 'Elemento'} agregado.`
    feedbackEsPositivo.value = true
  } catch (e: any) {
    feedback.value = e?.message || `Error al agregar ${(nivelesArray.value[0] ?? 'elemento').toLowerCase()}.`
    feedbackEsPositivo.value = false
    cancelarNivel1()
  } finally {
    loading.value = false
  }
}

// Formulario hijos
const mostrarFormHijo = ref(false)
const padreSeleccionado = ref<Componente | null>(null)

function cancelarHijo() {
  padreSeleccionado.value = null
  mostrarFormHijo.value = false
}

async function onAceptarHijo(payload: { nombre: string; descripcion: string; enfoque?: string; grupo_variante?: string | null }) {
  if (!padreSeleccionado.value) return
  try {
    loading.value = true

    const datos = {
      nombre: payload.nombre,
      peso: 0,
      descripcion: payload.descripcion || null,
      enfoque: payload.enfoque ?? 'general',
      grupo_variante: payload.grupo_variante ?? null
    }

    const result = await store.agregarHijo(padreSeleccionado.value.id_componente!, datos as any)
    if (!result.data) {
      feedback.value = result.error ?? `No fue posible crear el ${nombreNivelSiguiente.value.toLowerCase()}.`
      feedbackEsPositivo.value = false
      cancelarHijo()
      return
    }

    cancelarHijo()
    feedback.value = `${nombreNivelSiguiente.value} agregado.`
    feedbackEsPositivo.value = true
  } catch (e: any) {
    feedback.value = e?.message || `Error al agregar ${nombreNivelSiguiente.value.toLowerCase()}.`
    feedbackEsPositivo.value = false
    cancelarHijo() 
  } finally {
    loading.value = false
  }
}

// Formulario modificar
const mostrarFormModificar = ref(false)
const componenteEditando = ref<Componente | null>(null)
const nombreModificar = ref('')
const descripcionModificar = ref('')
const enfoqueModificar = ref<string | null>('general')
const grupoModificar = ref<string | null>('')

function cancelarModificar() {
  nombreModificar.value = ''
  descripcionModificar.value = ''
  enfoqueModificar.value = 'general'
  grupoModificar.value = ''
  componenteEditando.value = null
  mostrarFormModificar.value = false
}

// Eventos desde ModificarComponentes
function onModificarComponente(comp: Componente) {
  componenteEditando.value = comp
  nombreModificar.value = comp.nombre
  descripcionModificar.value = comp.descripcion ?? ''
  enfoqueModificar.value = comp.enfoque ?? 'general'
  grupoModificar.value = comp.grupo_variante ?? ''
  mostrarFormModificar.value = true
}

async function onAceptarModificar(payload: { nombre: string; descripcion: string; enfoque?: string; grupo_variante?: string | null }) {
  if (!componenteEditando.value) return
  try {
    loading.value = true

    const compActualizado: Componente = {
      ...componenteEditando.value,
      nombre: payload.nombre,
      descripcion: payload.descripcion || null,
      enfoque: payload.enfoque ?? 'general',
      grupo_variante: payload.grupo_variante ?? null
    }

    const pesoAnterior = compActualizado.peso
    const nivelNombre = nombreNivelDeComponente(componenteEditando.value!)
    const actualizado = await store.actualizar(compActualizado)
    if (!actualizado) {
      feedback.value = `Error al actualizar el ${nivelNombre.toLowerCase()}.`
      feedbackEsPositivo.value = false
      cancelarModificar()
      return
    }

    if (pesoAnterior !== actualizado.peso) {
      const padreId = actualizado.padre_id ?? actualizado.id_componente
      if (typeof padreId === 'number') {
        await store.redistribuirPesosEntreHermanos(
          padreId,
          actualizado.id_componente!,
          actualizado.peso ?? 0
        )
      }
    }

    cancelarModificar()
    feedback.value = `${nivelNombre} modificado.`
    feedbackEsPositivo.value = true
  } catch (e: any) {
    feedback.value = e?.message || 'Error al modificar el elemento.'
    feedbackEsPositivo.value = false
    cancelarModificar()
  } finally {
    loading.value = false
  }
}

function onEliminarComponente(comp: Componente) {
  compSeleccionado.value = comp
  mostrarConfirmarEliminar.value = true
}

async function ejecutarEliminar() {
  if (!compSeleccionado.value) return
  const nivelNombre = nombreNivelDeComponente(compSeleccionado.value)
  loadingAccion.value = true
  try {
    const ok = await store.eliminar(compSeleccionado.value.id_componente!)
    if (ok) {
      feedback.value = `${nivelNombre} eliminado.`
      feedbackEsPositivo.value = true
    } else {
      feedback.value = `No se pudo eliminar el ${nivelNombre.toLowerCase()}.`
      feedbackEsPositivo.value = false
    }
  } catch (e: any) {
    feedback.value = e?.message || `Error al eliminar el ${nivelNombre.toLowerCase()}.`
    feedbackEsPositivo.value = false
  } finally {
    loadingAccion.value = false
    mostrarConfirmarEliminar.value = false
    compSeleccionado.value = null
  }
}

function onAgregarHijo(payload: { padre: Componente; nivel: number }) {
  padreSeleccionado.value = payload.padre
  nombreNivelSiguiente.value = nivelesArray.value[payload.nivel + 1] ?? 'Componente'
  mostrarFormHijo.value = true
}

// Reordenar componente entre sus hermanos
async function reordenarComponente(componente: Componente, nuevaPosicion: number): Promise<void> {
  if (!componente.id_componente) return
  const nivelNombre = nombreNivelDeComponente(componente)

  if (componente.padre_id == null) {
    feedback.value = `El ${nivelNombre.toLowerCase()} raíz no puede reordenarse.`
    feedbackEsPositivo.value = false
    return
  }

  const hermanos = componentesAplanados.value.filter(c => c.ref.padre_id === componente.padre_id)

  if (hermanos.length < 2) {
    feedback.value = `El ${nivelNombre.toLowerCase()} no tiene elementos del mismo nivel con los que intercambiar posición.`
    feedbackEsPositivo.value = false
    return
  }

  if (nuevaPosicion < 1 || nuevaPosicion > hermanos.length) {
    feedback.value = `Posición inválida. Rango permitido: 1–${hermanos.length}.`
    feedbackEsPositivo.value = false
    return
  }

  loadingAccion.value = true
  try {
    const result = await store.reordenar(componente.id_componente, nuevaPosicion)
    if (result.ok) {
      feedback.value = `${nivelNombre} reordenado.`
      feedbackEsPositivo.value = true
      if (idComponenteRaiz.value != null) {
        await store.cargarArbolDesdeRaiz(idComponenteRaiz.value)
        componentesAplanados.value = aplanarComponentes(store.componentes)
      }
    } else {
      feedback.value = result.message ?? `No se pudo reordenar el ${nivelNombre.toLowerCase()}.`
      feedbackEsPositivo.value = false
    }
  } catch (e: any) {
    feedback.value = e?.message ?? `Error al reordenar el ${nivelNombre.toLowerCase()}.`
    feedbackEsPositivo.value = false
  } finally {
    loadingAccion.value = false
  }
}

function onReordenarComponente(payload: { dragged: Componente; target: Componente; side: 'before' | 'after' }) {
  const { dragged, target, side } = payload
  const hermanos = componentesAplanados.value
    .filter(c => c.ref.padre_id === dragged.padre_id)
    .sort((a, b) => (a.ref.ordinal ?? '').localeCompare(b.ref.ordinal ?? '', undefined, { numeric: true }))
  const reduced = hermanos.filter(c => c.ref.id_componente !== dragged.id_componente)
  const targetIdx = reduced.findIndex(c => c.ref.id_componente === target.id_componente)
  if (targetIdx === -1) return
  const insertIdx = side === 'before' ? targetIdx : targetIdx + 1
  reordenarComponente(dragged, insertIdx + 1)
}

function salir() {
  router.push({ name: 'ListaVersiones' })
}

// Completar modelo
async function CompletarModelo() {
  if (loadingAccion.value) return
  feedback.value = ''
  if (!idModelo.value) {
    feedback.value = 'No se pudo identificar el modelo.'
    feedbackEsPositivo.value = false
    return
  }

  loadingAccion.value = true
  try {
    if (sinVersion) {
      await versionStore.getVersiones()
      const tieneVersiones = versionStore.versiones.some(v => v.id_modelo === idModelo.value)
      if (!tieneVersiones) {
        const versionCreada = await versionStore.crearVersion({
          id_modelo: idModelo.value,
          nombre: modeloStore.modeloActual?.nombre ?? '',
          estado: 'edicion',
          fecha_creacion: new Date().toISOString().split('T')[0],
          fecha_aprobacion: null,
          ambito: null,
          id_programa: null
        })
        if (!versionCreada) {
          feedback.value = 'No se pudo crear la versión del modelo.'
          feedbackEsPositivo.value = false
          return
        }
      }
    }

    feedback.value = 'Modelo Finalizado'
    feedbackEsPositivo.value = true
    setTimeout(() => router.push({ name: 'ListaVersiones' }), 800)
  } catch (err: any) {
    console.error('Error al aprobar modelo:', err)
    feedback.value = err?.message || 'Error al completar el modelo.'
    feedbackEsPositivo.value = false
  } finally {
    loadingAccion.value = false
  }
}

function irAGestionPesos() {
  if (!idModelo.value) {
    feedback.value = 'No se pudo identificar el modelo.'
    feedbackEsPositivo.value = false
    return
  }
  router.push({ name: 'Pesos', params: { id_modelo: idModelo.value } })
}
</script>

<style scoped>
.formulario-modelo {
  background-color: white;
  padding: var(--space-lg);
  border-radius: var(--radius-lg);
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  margin: 20px;
  font-family: var(--font-family);
}

h1 {
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1.3;
}

.marco {
  border: 2px solid rgb(156, 155, 155);
  box-sizing: border-box;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.formulario-inicial {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.formulario-opcion {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  margin-bottom: var(--space-base);
}

input,
textarea {
  padding: 0.75rem;
  border: 1px solid #e6e5e5;
  border-radius: var(--radius-md);
  font-size: 1rem;
  font-family: var(--font-family);
  background-color: #f9f9f9;
  height: 40px;
  box-sizing: border-box;
  margin-bottom: var(--space-base);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

input:focus,
textarea:focus {
  outline: none;
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

textarea {
  height: auto;
}

.formulario-opcion span {
  color: #8f8e8e;
  margin-bottom: 0.5rem;
}

.formulario-descripcion {
  width: 97%;
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid #e6e5e5;
  border-radius: var(--radius-md);
  font-size: 1rem;
  font-family: var(--font-family);
  resize: vertical;
  background-color: #f9f9f9;
  flex: 1;
  display: flex;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.formulario-descripcion:focus {
  outline: none;
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

.input-personalizado {
  width: 200px;
}

.botones {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  height: 40px;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  transition: all 0.15s ease;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.btn.salir {
  background-color: white;
  border: 1px solid #ccc;
}

.btn.salir:hover {
  background-color: rgb(212, 26, 26);
  color: white;
}

.btn.guardar {
  background-color: #eea222;
  color: black;
}

.btn.guardar:hover {
  background-color: #0e69a0;
  cursor: pointer;
  color: white;
}

.divider {
  border: none;
  border-bottom: 1px solid #949393;
  margin: var(--space-lg) 0;
}

.feedback {
  margin-top: .5rem;
  color: #cf0808;
}

.componentes-lista {
  font-family: var(--font-family);
  font-weight: normal;
  padding: 0.5rem 0;
}

.componentes-lista ul {
  list-style: none;
  padding-left: 0;
  margin: 0;
}

.componentes-lista li {
  margin-bottom: 0.5rem;
}

.componentes-lista div {
  font-weight: normal;
  padding: 0.25rem 0;
  cursor: pointer;
}

.componentes-lista [data-nivel] {
  margin-left: calc((var(--nivel, 1) - 1) * 1rem);
}

.header-flex {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ctx-modelo {
  display: inline-block;
  margin: 6px 0 0;
  padding: 3px 10px;
  background: #e8f0f8;
  border-left: 3px solid #0e69a0;
  border-radius: 3px;
  font-size: 0.85rem;
  font-weight: 700;
  color: #111;
  letter-spacing: 0.01em;
}

.acciones-header {
  display: flex;
  gap: 0.5rem;
}

.formulario-inline {
  border: 1px dashed #e0e0e0;
  border-radius: 6px;
  padding: 12px;
  background: #fafafa;
}

.mensaje-positivo {
  color: #0a7d00;
  font-weight: bold;
}

.mensaje-error {
  color: #b00020;
  font-weight: bold;
}

.mensaje-vacio {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  text-align: center;
}

.mensaje-vacio p {
  font-size: 1.5rem;
  color: #888;
  font-weight: 600;
}

label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  font-size: var(--font-size-sm);
}

label small {
  font-weight: normal;
  font-size: 0.85em;
  color: #666;
  margin-left: 4px;
}

.filtro-botones {
  display: flex;
  gap: 0.5rem;
  margin: 1rem 0;
  flex-wrap: wrap;
}

.btn-filtro {
  padding: 0.5rem 1.2rem;
  border-radius: 20px;
  border: 1px solid #ccc;
  background-color: #f9f9f9;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.2s ease;
  font-family: var(--font-family);
}

.btn-filtro:hover {
  background-color: #eea222;
  color: white;
}

.btn-filtro.activo {
  background-color: #0e69a0;
  color: white;
  border-color: #0e69a0;
}

.filtro-select {
  margin-bottom: 1rem;
}

.filtro-select label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  font-size: 0.95rem;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.95rem;
  font-family: var(--font-family);
  background-color: white;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.form-control:focus {
  outline: none;
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}
</style>
